import type { Data, Time } from "convert";
import type { ExpandRecursively, ValueOf } from "./extensions";
import type {
  DeployStatus as DeployStatusGraphQL,
  Maybe,
  Role,
  GenericExporter,
  MetricExporter,
  CompressionStats as _CompressionStats,
  ParameterUnit,
  User,
  Service,
  MfaMethod,
  SetReplicaCountInput,
  NumericParameter,
  StringParameter,
  RbacRole,
  CloudProvider,
  ProjectMember,
} from "../graphql/generated";
import {
  Type as TypeGraphQL,
  Status as StatusGraphQL,
} from "../graphql/generated";

export type ServiceStatus = ValueOf<typeof DeployStatusGraphQL>;

export type ServiceSpec = Service["resources"][0]["spec"];

export const ServiceType = {
  Postgres: TypeGraphQL.Postgres,
  Timescaledb: TypeGraphQL.Timescaledb,
  Vector: TypeGraphQL.Vector,
};

export type ProductID =
  | "product_pg"
  | "product_pg_storage"
  | "product_tsdb"
  | "product_tsdb_storage"
  | "product_data_tiering"
  | "product_vpc_attachment"
  | "product_elastic_storage"
  | "product_fluid_storage"
  | "product_pg_elastic_storage"
  | "product_connection_pooler"
  | "product_vector"
  | "product_vector_elastic_storage"
  | "product_ioboost_virtual_addon"
  | "product_transit_gateway_peering"
  | "product_connector_s3_compute"
  | "product_private_link_traffic";

export const ServiceTypeProductId = {
  [ServiceType.Timescaledb]: {
    Config: "product_tsdb",
    Storage: "product_elastic_storage",
    DataTiering: "product_data_tiering",
    ConnectionPooler: "product_connection_pooler",
  },
  [ServiceType.Postgres]: {
    Config: "product_pg",
    Storage: "product_pg_elastic_storage",
    DataTiering: "product_data_tiering",
    ConnectionPooler: "product_connection_pooler",
  },
  [ServiceType.Vector]: {
    Config: "product_vector",
    Storage: "product_vector_elastic_storage",
    DataTiering: "product_data_tiering",
    ConnectionPooler: "product_connection_pooler",
  },
};

export type ReplicaRoleType = 0 | 1 | null;

export const ToggleServiceStatus = StatusGraphQL;

export const VPC_SIMPLE_CONNECTION_STATUS = {
  READY: "READY",
  IN_PROGRESS: "IN_PROGRESS",
  ERROR: "ERROR",
} as const;

export const CONNECTION = {
  ONLINE: "online",
  OFFLINE: "offline",
} as const;

export enum RegionCode {
  US_EAST_1 = "us-east-1",
  US_EAST_2 = "us-east-2",
  US_WEST_2 = "us-west-2",
  EU_WEST_1 = "eu-west-1",
  EU_WEST_2 = "eu-west-2",
  EU_WEST_3 = "eu-west-3",
  EU_CENTRAL_1 = "eu-central-1",
  EU_CENTRAL_2 = "eu-central-2",
  AP_SOUTHEAST_2 = "ap-southeast-2",
  AP_SOUTHEAST_1 = "ap-southeast-1",
  AP_NORTHEAST_1 = "ap-northeast-1",
  CA_CENTRAL_1 = "ca-central-1",
  SA_EAST_1 = "sa-east-1",
  AP_SOUTH_1 = "ap-south-1",
  AZ_EASTUS = "az-eastus",
  AZ_EASTUS2 = "az-eastus2",
  AZ_WESTEUROPE = "az-westeurope",
  AZ_GERMANYWESTCENTRAL = "az-germanywestcentral",
  AZ_SOUTHEASTASIA = "az-southeastasia",
  GCP_US_CENTRAL_1 = "gcp-us-central1",
  GCP_EUROPE_WEST_1 = "gcp-europe-west1",
}

// These are the regions used by "Intl" JS module. You can see them in action
// by running "Intl.DateTimeFormat().resolvedOptions().timeZone" or
// "Intl.supportedValuesOf('timeZone')" in your browser.
export enum TimezoneRegion {
  Africa = "Africa",
  America = "America",
  Antarctica = "Antarctica",
  Arctic = "Arctic",
  Asia = "Asia",
  Atlantic = "Atlantic",
  Australia = "Australia",
  Europe = "Europe",
  Indian = "Indian",
  Pacific = "Pacific",
}

// An SVG typing for fill on top of CSS.
export type Path = {
  path: {
    fill: string;
  };
};

export type Member = {
  userId: Maybe<string>;
  inviteId: Maybe<string>;
  joined: Maybe<string>;
  created: Maybe<string>;
  email: string;
  declined: Maybe<string>;
  role: Role;
  rbacRole: RbacRole;
  name: Maybe<string>;
  mfaMethods: MfaMethod[];
  hasSSO: boolean;
  lastLoginSession: ProjectMember["lastLoginSession"];
};

export type ExporterDataType = "METRICS" | "LOGS";

export type ExporterType =
  | "DATADOG"
  | "CLOUDWATCH"
  | "PROMETHEUS"
  | "UNSPECIFIED"
  | "UNKNOWN"
  | "AZUREMONITOR";

export type ConfigGenericExporter = {
  awsAccessKey?: string;
  awsSecretKey?: string;
  logStreamName?: string;
  logGroupName?: string;
  awsRoleArn?: string;
  site?: string;
  user?: string;
  password?: string;
};

export type Exporter =
  | ({
      exporterUuid?: string;
    } & GenericExporter)
  | (MetricExporter & {
      dataType?: ExporterDataType;
      config?: ConfigGenericExporter;
    });

export type HubspotField = {
  name: string;
  value: any;
};

export type HubspotUserProps = {
  is_invited: boolean;
  wants_subscription: boolean;
  utm_params: Record<string, string>;
};

export type HubspotFormProps = Pick<User, "name" | "email" | "id"> & {
  isVerified?: boolean;
  submissionPage?: string;
};

// re-declaring from "zen-observable-ts" because it's a peer instead of direct dependency
export type Subscription = {
  closed: boolean;
  unsubscribe(): void;
};

export type ChakraSizes = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

export enum UTM_CAMPAIGNS {
  VECTOR_LAUNCH = "vectorlaunch", // 2023-09-15
  TIME_SERIES = "time-series",
  WELCOME_BACK = "welcomeback",
}

// Removing deprecated "ratio" property.
// See original `CompressionStats` in `/graphql/generated.ts`
export type CompressionStats = Omit<_CompressionStats, "ratio"> & {
  ratio?: _CompressionStats["ratio"];
};

type ParamOption = {
  value: ParameterUnit[keyof ParameterUnit];
  label: string;
  rate: Time | Data;
};

export const TIME: ParamOption[] = [
  { value: "MICROSECONDS", label: "𝝁s", rate: "μs" },
  { value: "MILLISECONDS", label: "ms", rate: "ms" },
  { value: "SECONDS", label: "sec", rate: "s" },
  { value: "MINUTES", label: "min", rate: "min" },
  { value: "HOURS", label: "hours", rate: "h" },
  { value: "DAYS", label: "days", rate: "d" },
];

// The values are labeled incorrectly (should be KIBIBYTES, MEBIBYTES) but have to be set this way to match the backend
export const SIZE: ParamOption[] = [
  { value: "BYTES", label: "bytes", rate: "B" },
  { value: "KILOBYTES", label: "KiB", rate: "KiB" },
  { value: "MEGABYTES", label: "MiB", rate: "MiB" },
];

// Types to use with reducers/useReducer

/**
 * @typeParam *P* - the type for all the payloads that are to be
 * expected. The keys should be the names of the actions,
 * and the values should be the types of the payloads.
 * Specify a type of "never" for actions that don't have a payload.
 *
 * @example
 * type PayloadTypes = {
      SELECT_ATTRIBUTE: Attribute;
      DESELECT_ALL_ATTRIBUTES: never;
      DESELECT_ATTRIBUTE: { value: string };
    }
 *
 * @typeParam *T* - the name of the action, which should exist
 * as a key in P, the payloads type.
 *
 * @example
 * "SELECT_ATTRIBUTE"
 */
export interface Action<T extends keyof P, P extends { [K in keyof P]: P[K] }> {
  type: T;
  payload: P[T];
}

export type ActionWithoutPayload<
  T extends keyof P,
  P extends { [K in keyof P]: P[K] },
> = Omit<Action<T, P>, "payload">;

export type SetReplicaCount = Omit<
  SetReplicaCountInput,
  "serviceId" | "projectId"
>;

export type DatabaseParameter = ExpandRecursively<
  NumericParameter | StringParameter
>;

export type RegionList = {
  id: RegionCode;
  value: RegionCode;
  label: string;
  cloud_provider: CloudProvider;
};

export enum RecommendationFeedback {
  X_CLOSE = "X_CLOSE",
  MARK_AS_COMPLETE = "MARK_AS_COMPLETE",
  NOT_RELEVANT = "NOT_RELEVANT",
  ANNOYING = "ANNOYING",
}

export enum ServiceRole {
  PRIMARY = "PRIMARY",
  REPLICA = "REPLICA",
}

export type ServiceExtended = Service & {
  initialPassword?: string;
};
